import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, money, errMsg } from "@/lib/api";
import { RecordedSave } from "@/admin/Recorded";
import { AdminTitle, Btn, Pill, Table } from "@/admin/AdminLayout";
import { inputCls } from "@/components/common";

const toDict = (list: any) => Object.fromEntries((Array.isArray(list) ? list : []).map((t: any) => [t.url, t.tone]));
const TONE_LABEL: Record<string, string> = { white: "Platinum / White gold", yellow: "Yellow gold", rose: "Rose gold", all: "All metals (lifestyle)" };

function ToneGrid({ p, value, onChange }: any) {
  const auto = toDict(p.image_tones);
  return (
    <div data-testid="image-tone-grid">
      <p className="eyebrow text-[10px] text-neutral-500 mb-2">Photo per metal (auto-detected; override if needed)</p>
      <div className="grid grid-cols-3 gap-3">
        {(p.images || []).map((u: string, i: number) => (
          <div key={u} className="border border-neutral-200 p-2">
            <img src={u} alt="" className="h-20 w-full object-contain bg-neutral-50" />
            <select value={value[u] || ""} onChange={(e) => { const v = { ...value }; if (e.target.value) v[u] = e.target.value; else delete v[u]; onChange(v); }}
              className="w-full mt-2 border px-1 py-1 text-[11px]" data-testid={`image-tone-select-${i}`}>
              <option value="">Auto: {TONE_LABEL[auto[u]] || "pending"}</option>
              {Object.entries(TONE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminProducts() {
  const [items, setItems] = useState<any[]>([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState<any>(null);
  const [params, setParams] = useSearchParams();
  const openEdit = (p: any) => setEdit({ ...p, form: { badge: "", seo_title: "", seo_description: "", extra_content: "", noindex: false, ...p.cms, image_tone_overrides: toDict(p.cms?.image_tone_overrides) } });
  const load = () => api.get("/admin-x/products").then((r) => { setItems(r.data); return r.data; });
  useEffect(() => {
    load().then((list: any[]) => {
      const p = params.get("edit") && list.find((x) => x.sku === params.get("edit"));
      if (p) { openEdit(p); setParams({}, { replace: true }); }
    });
  }, []);

  const saveCms = async (p: any, cms: any) => {
    const body = { hidden: false, featured: false, ...p.cms, image_tone_overrides: toDict(p.cms?.image_tone_overrides), ...cms };
    try { await api.put(`/admin-x/products/${p.id}/cms`, body); load(); toast("Saved"); return true; } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const shown = items.filter((p) => !q || `${p.name} ${p.sku} ${p.product_type}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="admin-products">
      <AdminTitle title="Products" sub="Synced from the lookbook. Catalog fields are read-only; merchandising and SEO below are CMS-managed and never overwritten by sync.">
        <input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="border border-neutral-300 bg-white px-3 py-2 text-sm" data-testid="admin-product-search" />
      </AdminTitle>
      <Table head={["", "Product", "Type", "Price", "Source", "Storefront", "Featured", "Hidden", ""]} testId="admin-products-table">
        {shown.map((p) => {
          const live = p.published && p.available && !p.cms?.hidden;
          return (
            <tr key={p.id} data-testid={`admin-product-row-${p.sku}`}>
              <td className="px-4 py-2"><img src={p.image} alt="" className="h-12 w-12 object-contain bg-neutral-50" /></td>
              <td className="px-4 py-2"><a href={`/products/${p.slug}`} target="_blank" rel="noreferrer" className="hover:underline">{p.name}</a><div className="text-xs text-neutral-500">{p.sku}</div></td>
              <td className="px-4 py-2 text-xs">{p.product_type}</td>
              <td className="px-4 py-2">{money(p.price)}</td>
              <td className="px-4 py-2">{!p.available ? <Pill tone="red">removed at source</Pill> : p.website_visible ? <Pill tone="green">website</Pill> : <Pill>lookbook only</Pill>}</td>
              <td className="px-4 py-2">{live ? <Pill tone="green">live</Pill> : <Pill>not shown</Pill>}</td>
              <td className="px-4 py-2"><input type="checkbox" checked={!!p.cms?.featured} onChange={(e) => saveCms(p, { featured: e.target.checked })} data-testid={`featured-toggle-${p.sku}`} /></td>
              <td className="px-4 py-2"><input type="checkbox" checked={!!p.cms?.hidden} onChange={(e) => saveCms(p, { hidden: e.target.checked })} data-testid={`hidden-toggle-${p.sku}`} /></td>
              <td className="px-4 py-2"><Btn ghost onClick={() => openEdit(p)} data-testid={`product-seo-edit-${p.sku}`}>SEO & Content</Btn></td>
            </tr>
          );
        })}
      </Table>
      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-cms-dialog">
          <DialogTitle className="font-display text-2xl">{edit?.name}</DialogTitle>
          {edit && (
            <div className="space-y-3">
              {[["badge", "Badge (e.g. Bestseller)"], ["seo_title", "SEO title"], ["seo_description", "SEO description"]].map(([k, l]) => (
                <input key={k} placeholder={l} value={edit.form[k] || ""} onChange={(e) => setEdit({ ...edit, form: { ...edit.form, [k]: e.target.value } })} className={inputCls} data-testid={`product-cms-${k}`} />
              ))}
              <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={!!edit.form.noindex} onChange={(e) => setEdit({ ...edit, form: { ...edit.form, noindex: e.target.checked } })} data-testid="product-cms-noindex" />Hide from Google (noindex)</label>
              <textarea rows={5} placeholder="Extra product page content (## headings supported)" value={edit.form.extra_content || ""} onChange={(e) => setEdit({ ...edit, form: { ...edit.form, extra_content: e.target.value } })} className={inputCls} data-testid="product-cms-extra_content" />
              <ToneGrid p={edit} value={edit.form.image_tone_overrides} onChange={(v: any) => setEdit({ ...edit, form: { ...edit.form, image_tone_overrides: v } })} />
              <RecordedSave res="products" id={edit.id} onSave={() => saveCms(edit, edit.form)} value={edit.form} testId="product-cms-save" onRestored={async () => { const list = await load(); const p = list.find((x: any) => x.id === edit.id); if (p) openEdit(p); }} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
